import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pt } from 'date-fns/locale';

import PageHeader from '../../components/pageHeader';
import { Alerta } from '../../components/alerta';
import SubmitButton from '../../components/submitButton';
import { formatMonthYearLabelPtBr } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/customerNotification.css';

const notificationTypes = [
    { value: 'NovoCliente', label: 'Novo cliente' },
    { value: 'TrocaTitularidade', label: 'Troca de titularidade' },
    { value: 'Faturamento', label: 'Faturamento' }
];

const hasNumericValue = (value) => value !== '' && value !== null && !Number.isNaN(Number(value));

const NotificarCliente = () => {
    const [cliente, setCliente] = useState('');
    const [clienteId, setClienteId] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [ccAntiga, setCCAntiga] = useState('');
    const [ccNova, setCCNova] = useState('');
    const [clienteBT, setClienteBT] = useState(true);
    const [error, setError] = useState(false);
    const [valorCobrar, setValorCobrar] = useState('');
    const [cc, setCC] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [whatsAppChecked, setWhatsAppChecked] = useState(false);
    const [emailChecked, setEmailChecked] = useState(false);
    const [openAlerta, setOpenAlerta] = useState(false);
    const [tipoNotificacao, setTipoNotificacao] = useState('');
    const [isSubmit, setIsSubmit] = useState(false);

    async function getClientInfo () {
        if (!clienteId) {
            return;
        }

        const body = JSON.stringify({
            cliente_id: clienteId,
            tipo: 'contato'
        });

        const response = await fetchFlask('getClientData', body);

        if (response.status !== 'error') {
            setCliente(response[0].cliente);
        } else {
            handleAlerta(response);
        }
    };

    async function submitNotificarCliente (e) {
        e.preventDefault();
        setIsSubmit(true);

        if (!dateTime && tipoNotificacao === 'Faturamento') {
            setError(true);
            setIsSubmit(false);
            return;
        }
        setError(false);

        let body = {};
        if (tipoNotificacao === 'TrocaTitularidade') {
            body = JSON.stringify({
                tipoNotificacao,
                ccAntiga,
                ccNova,
                notificarWhatsApp: whatsAppChecked,
                notificarEmail: emailChecked
            });
        } else if (tipoNotificacao === 'NovoCliente') {
            body = JSON.stringify({
                tipoNotificacao,
                cliente,
                cliente_id: Number(clienteId),
                clienteBT,
                notificarWhatsApp: whatsAppChecked,
                notificarEmail: emailChecked
            });
        } else if (tipoNotificacao === 'Faturamento') {
            const ano = dateTime.getFullYear();
            const mes = dateTime.getMonth() + 1;
            const periodo = ano + '-' + mes;

            body = JSON.stringify({
                tipoNotificacao,
                cc,
                periodo,
                valorCobrar
            });
        };

        const response = await fetchFlask('notificarCliente', body);
        handleAlerta(response);
        setIsSubmit(false);
    };

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    const tipoNotificacaoLabel = notificationTypes.find((tipo) => tipo.value === tipoNotificacao)?.label || 'Não selecionada';
    const periodoLabel = formatMonthYearLabelPtBr(dateTime, 'Pendente');
    const valorLabel = hasNumericValue(valorCobrar) ? formatCurrency(Number(valorCobrar)) : 'Não informado';
    const canaisSelecionados = [
        whatsAppChecked && 'WhatsApp',
        emailChecked && 'Email'
    ].filter(Boolean);
    const canaisLabel = tipoNotificacao === 'Faturamento'
        ? 'Fluxo de faturamento'
        : canaisSelecionados.length > 0 ? canaisSelecionados.join(' + ') : 'Não selecionado';
    const contextLabel = tipoNotificacao === 'NovoCliente'
        ? clienteBT ? 'BT' : 'AT ou BTOP'
        : tipoNotificacao === 'TrocaTitularidade'
            ? `${ccAntiga || 'CC antiga pendente'} -> ${ccNova || 'CC nova pendente'}`
            : tipoNotificacao === 'Faturamento'
                ? `${cc || 'CC pendente'} / ${periodoLabel}`
                : 'Aguardando tipo';
    const completedFields = [
        clienteId,
        cliente,
        tipoNotificacao,
        tipoNotificacao === 'NovoCliente' ? true : null,
        tipoNotificacao === 'TrocaTitularidade' ? ccAntiga && ccNova : null,
        tipoNotificacao === 'Faturamento' ? cc && dateTime && hasNumericValue(valorCobrar) : null,
        tipoNotificacao && tipoNotificacao !== 'Faturamento' ? (whatsAppChecked || emailChecked) : null
    ].filter(Boolean).length;
    const totalCriticalFields = tipoNotificacao === 'Faturamento' ? 4 : tipoNotificacao ? 5 : 3;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Notificar Cliente</PageHeader>

            <form action='' method='POST' id='formNotificarCliente' onSubmit={submitNotificarCliente} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Comunicação com o cliente</h2>
                        </Box>
                        <p>
                            Busque o cliente, escolha o tipo de notificação e revise os dados antes do disparo.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <ManageAccountsRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Cliente</strong>
                            </Box>
                        </Box>

                        <Box className='ui-lookup-grid'>
                            <TextField
                                required
                                label='Matrícula / Cliente ID'
                                value={clienteId}
                                onChange={(e) => setClienteId(e.target.value)}
                            />
                            <Button
                                type='button'
                                variant='contained'
                                onClick={getClientInfo}
                                startIcon={<PersonSearchRoundedIcon />}
                            >
                                Buscar Cliente
                            </Button>
                        </Box>

                        <Box className='customer-notification-client-strip'>
                            <NotificationsActiveRoundedIcon />
                            <Box>
                                <span>Cliente localizado</span>
                                <strong>{cliente || 'Aguardando busca'}</strong>
                            </Box>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <NotificationsActiveRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Tipo de notificação</strong>
                            </Box>
                        </Box>

                        <FormControl required className='ui-control'>
                            <InputLabel id='labelNotificacao'>Notificação</InputLabel>
                            <Select
                                id='selectNotificacao'
                                labelId='labelNotificacao'
                                label='Notificação'
                                value={tipoNotificacao}
                                onChange={(e) => setTipoNotificacao(e.target.value)}
                            >
                                {notificationTypes.map((tipo) => (
                                    <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {tipoNotificacao === 'NovoCliente' && (
                        <Box className='ui-workflow-panel'>
                            <Box className='ui-heading'>
                                <FactCheckRoundedIcon />
                                <Box>
                                    <span>Etapa 3</span>
                                    <strong>Perfil do cliente</strong>
                                </Box>
                            </Box>

                            <Box className='customer-notification-checkbox-card'>
                                <FormControlLabel
                                    className='customer-notification-checkbox-control'
                                    control={
                                        <Checkbox
                                            checked={clienteBT}
                                            tabIndex={-1}
                                            disableRipple
                                            onChange={() => setClienteBT(!clienteBT)}
                                        />
                                    }
                                    label={
                                        <Box className='customer-notification-checkbox-copy'>
                                            <strong>{clienteBT ? 'BT' : 'AT ou BTOP'}</strong>
                                            <span>Alterne para indicar o perfil usado na comunicação de novo cliente.</span>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Box>
                    )}

                    {tipoNotificacao === 'TrocaTitularidade' && (
                        <Box className='ui-workflow-panel'>
                            <Box className='ui-heading'>
                                <SwapHorizRoundedIcon />
                                <Box>
                                    <span>Etapa 3</span>
                                    <strong>Contas contrato</strong>
                                </Box>
                            </Box>

                            <Box className='ui-fields-grid'>
                                <TextField
                                    required
                                    label='CC antiga'
                                    id='ccAntiga'
                                    value={ccAntiga}
                                    onChange={(e) => setCCAntiga(e.target.value)}
                                />
                                <TextField
                                    required
                                    label='CC nova'
                                    id='ccNova'
                                    value={ccNova}
                                    onChange={(e) => setCCNova(e.target.value)}
                                />
                            </Box>
                        </Box>
                    )}

                    {tipoNotificacao === 'Faturamento' && (
                        <Box className='ui-workflow-panel'>
                            <Box className='ui-heading'>
                                <PaymentsRoundedIcon />
                                <Box>
                                    <span>Etapa 3</span>
                                    <strong>Dados de faturamento</strong>
                                </Box>
                            </Box>

                            <Box className='ui-fields-grid'>
                                <TextField
                                    required
                                    label='Conta contrato'
                                    id='cc'
                                    value={cc}
                                    onChange={(e) => setCC(e.target.value)}
                                />
                                <FormControl className='ui-control' error={error}>
                                    <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                        <DatePicker id='data' label='Período'
                                            views={['month', 'year']}
                                            openTo='month'
                                            minDate={new Date('2023-01-02')}
                                            maxDate={new Date()}
                                            value={dateTime}
                                            onChange={(value) => setDateTime(value)}
                                            slotProps={{
                                                openPickerButton: {
                                                    color: 'primary'
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                    {error && (
                                        <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                    )}
                                </FormControl>
                                <TextField
                                    required
                                    label='Valor da conta'
                                    type='number'
                                    inputProps={{ step: 0.01, min: 0 }}
                                    value={valorCobrar}
                                    onChange={(e) => setValorCobrar(e.target.value)}
                                />
                            </Box>
                        </Box>
                    )}

                    {tipoNotificacao && tipoNotificacao !== 'Faturamento' && (
                        <Box className='ui-workflow-panel'>
                            <Box className='ui-heading'>
                                <SendRoundedIcon />
                                <Box>
                                    <span>Etapa 4</span>
                                    <strong>Canais de envio</strong>
                                </Box>
                            </Box>

                            <Box className='customer-notification-channel-grid'>
                                <FormControlLabel
                                    className='customer-notification-channel-card'
                                    control={
                                        <Checkbox
                                            checked={whatsAppChecked}
                                            tabIndex={-1}
                                            disableRipple
                                            onChange={() => setWhatsAppChecked(!whatsAppChecked)}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <WhatsAppIcon />
                                            <strong>WhatsApp</strong>
                                        </Box>
                                    }
                                />

                                <FormControlLabel
                                    className='customer-notification-channel-card'
                                    control={
                                        <Checkbox
                                            checked={emailChecked}
                                            tabIndex={-1}
                                            disableRipple
                                            onChange={() => setEmailChecked(!emailChecked)}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <AlternateEmailRoundedIcon />
                                            <strong>Email</strong>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Box>
                    )}

                    <Box className='ui-rail'>
                        <Box>
                            <NotificationsActiveRoundedIcon />
                            <span>Tipo</span>
                            <strong>{tipoNotificacaoLabel}</strong>
                        </Box>
                        <Box>
                            <SendRoundedIcon />
                            <span>Canais</span>
                            <strong>{canaisLabel}</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da notificação</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Matrícula / Cliente ID</span>
                            <strong className={!clienteId ? 'is-empty' : undefined}>{clienteId || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Cliente</span>
                            <strong className={!cliente ? 'is-empty' : undefined}>{cliente || 'Não localizado'}</strong>
                        </Box>
                        <Box>
                            <span>Notificação</span>
                            <strong className={!tipoNotificacao ? 'is-empty' : undefined}>{tipoNotificacaoLabel}</strong>
                        </Box>
                        <Box>
                            <span>Contexto</span>
                            <strong>{contextLabel}</strong>
                        </Box>
                        {tipoNotificacao === 'Faturamento' && (
                            <Box>
                                <span>Valor</span>
                                <strong className={!hasNumericValue(valorCobrar) ? 'is-empty' : undefined}>{valorLabel}</strong>
                            </Box>
                        )}
                    </Box>

                    <Box className='ui-attention'>
                        <NotificationsActiveRoundedIcon />
                        <p>
                            Revise tipo, contexto e canais antes de enviar. O conteúdo disparado muda conforme a notificação selecionada.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/{totalCriticalFields} preenchidos</strong>
                        </Box>
                    </Box>

                    {tipoNotificacao && (
                        <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                            Notificar Cliente
                        </SubmitButton>
                    )}
                </Box>
            </form>
        </Box>
    );
};

export default NotificarCliente;
